import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, Clock, XCircle, AlertCircle,
  Send, Loader2, Upload, MessageCircle, FileText, X,
  Eye, EyeOff, Plus, Trash2
} from 'lucide-react';
import { projectsApi } from '../utils/api';

const statusConfig = {
  pending: { label: 'Not Started', color: '#6b7280', icon: Clock },
  submitted: { label: 'Submitted', color: '#3b82f6', icon: Clock },
  pending_review: { label: 'Pending Review', color: '#f59e0b', icon: AlertCircle },
  approved: { label: 'Approved', color: '#22c55e', icon: CheckCircle },
  rejected: { label: 'Needs Revision', color: '#f59e0b', icon: XCircle },
};

const COMPOSITE_TYPES = ['address', 'primary_contact', 'nas', 'domain_controller', 'server_list', 'vm_list'];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC'
];

const defaultResponseData = (fieldType) => {
  switch (fieldType) {
    case 'address':
      return { street: '', city: '', state: '', zip: '' };
    case 'primary_contact':
      return { first_name: '', last_name: '', email: '', phone: '' };
    case 'nas':
      return { has_nas: null, brand: '', model: '', hostname: '', ip_address: '', username: '', password: '' };
    case 'domain_controller':
      return { has_domain_controller: false, domain_admin_username: '', domain_admin_password: '' };
    case 'server_list':
      return { has_servers: null, servers: [{ name: '', os: '', ip_address: '', domain_joined: false, local_admin_username: '', local_admin_password: '' }] };
    case 'vm_list':
      return { has_hypervisor: null, hypervisor_name: '', hypervisor_type: '', hypervisor_ip: '', hypervisor_username: '', hypervisor_password: '', vms: [{ name: '', guest_os: '', ip_address: '', cpu_cores: '', ram_gb: '', storage_gb: '', notes: '' }] };
    default:
      return {};
  }
};

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('questions');
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  const [activeQuestion, setActiveQuestion] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [responseData, setResponseData] = useState(null);
  const [otherText, setOtherText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [showPreviewPasswords, setShowPreviewPasswords] = useState({});

  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    try {
      const [questionsData, messagesData, deliverablesData] = await Promise.all([
        projectsApi.getQuestions(projectId),
        projectsApi.getMessages(projectId),
        // Deliverables are non-critical — if the call fails, keep the rest.
        projectsApi.getDeliverables(projectId).catch(() => []),
      ]);
      setQuestions(questionsData);
      setMessages(messagesData);
      setDeliverables(Array.isArray(deliverablesData) ? deliverablesData : []);
    } catch (err) {
      setError('Failed to load project data.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadDeliverable = async (deliverable) => {
    try {
      const data = await projectsApi.downloadDeliverable(projectId, deliverable.id);
      window.open(data.download_url, '_blank', 'noopener');
    } catch (err) {
      // Most likely a 410 (expired between list + click) — refresh so the UI
      // reflects the expired state and show the server message.
      setError(err.message || 'This download is no longer available.');
      loadData();
    }
  };

  const handleQuestionClick = (question) => {
    if (question.current_response?.status === 'approved') return;
    setActiveQuestion(question);
    setShowPasswords({});
    setUploadError('');
    setSelectedFile(null);

    if (COMPOSITE_TYPES.includes(question.field_type)) {
      setResponseData(question.current_response?.response_data || defaultResponseData(question.field_type));
      setResponseText('');
      setOtherText('');
    } else if (question.field_type === 'dropdown') {
      const opts = question.dropdown_options || [];
      const existing = question.current_response?.response_text || '';
      if (opts.includes(existing)) {
        setResponseText(existing);
        setOtherText('');
      } else if (existing) {
        setResponseText('Other');
        setOtherText(existing);
      } else {
        setResponseText('');
        setOtherText('');
      }
      setResponseData(null);
    } else {
      setResponseText(question.current_response?.response_text || '');
      setResponseData(null);
      setOtherText('');
    }
  };

  const updateResponseData = (key, value) => {
    setResponseData(prev => ({ ...prev, [key]: value }));
  };

  const updateServer = (index, key, value) => {
    setResponseData(prev => {
      const servers = [...prev.servers];
      servers[index] = { ...servers[index], [key]: value };
      return { ...prev, servers };
    });
  };

  const addServer = () => {
    setResponseData(prev => ({
      ...prev,
      servers: [...prev.servers, { name: '', os: '', ip_address: '', domain_joined: false, local_admin_username: '', local_admin_password: '' }]
    }));
  };

  const removeServer = (index) => {
    setResponseData(prev => ({
      ...prev,
      servers: prev.servers.filter((_, i) => i !== index)
    }));
  };

  const updateVM = (index, key, value) => {
    setResponseData(prev => {
      const vms = [...(prev.vms || [])];
      vms[index] = { ...vms[index], [key]: value };
      return { ...prev, vms };
    });
  };

  const addVM = () => {
    setResponseData(prev => ({
      ...prev,
      vms: [...(prev.vms || []), { name: '', guest_os: '', ip_address: '', cpu_cores: '', ram_gb: '', storage_gb: '', notes: '' }]
    }));
  };

  const removeVM = (index) => {
    setResponseData(prev => ({
      ...prev,
      vms: prev.vms.filter((_, i) => i !== index)
    }));
  };

  const togglePassword = (key) => {
    setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const canSubmit = () => {
    const ft = activeQuestion?.field_type;
    if (ft === 'document') return false;
    if (COMPOSITE_TYPES.includes(ft)) {
      if (!responseData) return false;
      if (ft === 'address') return responseData.street && responseData.city && responseData.state && responseData.zip;
      if (ft === 'primary_contact') return responseData.first_name && responseData.last_name && responseData.email;
      if (ft === 'nas') {
        if (responseData.has_nas === null) return false;
        if (!responseData.has_nas) return true;
        return responseData.brand && responseData.model && responseData.hostname && responseData.ip_address;
      }
      if (ft === 'domain_controller') return true;
      if (ft === 'server_list') {
        if (responseData.has_servers === null) return false;
        if (!responseData.has_servers) return true;
        return responseData.servers?.length > 0 && responseData.servers.every(s => s.name && s.os && s.ip_address);
      }
      if (ft === 'vm_list') {
        if (responseData.has_hypervisor === null) return false;
        if (!responseData.has_hypervisor) return true;
        return !!responseData.hypervisor_name && responseData.vms?.length > 0 && responseData.vms.every(v => v.name && v.guest_os);
      }
      return true;
    }
    if (ft === 'dropdown') return responseText === 'Other' ? otherText.trim().length > 0 : responseText.length > 0;
    return responseText.trim().length > 0;
  };

  const handleSubmitResponse = async () => {
    if (!activeQuestion || !canSubmit()) return;

    setIsSubmitting(true);
    try {
      const ft = activeQuestion.field_type;
      if (COMPOSITE_TYPES.includes(ft)) {
        await projectsApi.submitResponse(projectId, activeQuestion.id, null, responseData);
      } else {
        const text = (ft === 'dropdown' && responseText === 'Other') ? otherText : responseText;
        await projectsApi.submitResponse(projectId, activeQuestion.id, text, null);
      }
      await loadData();
      setActiveQuestion(null);
      setResponseText('');
      setResponseData(null);
      setOtherText('');
    } catch (err) {
      setError('Failed to submit response.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      setUploadError('File size must be under 25MB');
      return;
    }
    setSelectedFile(file);
    setUploadError('');
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleUploadDocument = async () => {
    if (!activeQuestion || !selectedFile) return;

    setIsUploading(true);
    setUploadError('');
    try {
      await projectsApi.uploadDocument(projectId, activeQuestion.id, selectedFile);
      await loadData();
      setActiveQuestion(null);
      setSelectedFile(null);
    } catch (err) {
      setUploadError(err.message || 'Failed to upload document');
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    setIsSending(true);
    try {
      await projectsApi.sendMessage(projectId, newMessage);
      setNewMessage('');
      await loadData();
    } catch (err) {
      setError('Failed to send message.');
    } finally {
      setIsSending(false);
    }
  };

  const groupQuestionsByCategory = () => {
    const groups = {};
    questions.forEach((q) => {
      const category = q.category || 'General';
      if (!groups[category]) groups[category] = [];
      groups[category].push(q);
    });
    return groups;
  };

  const availableDeliverableCount = deliverables.filter((d) => d.available).length;

  // ── Field type renderers ──────────────────────────────────────────────────

  const renderPasswordField = (passwordKey, value, onChange, placeholder = 'Password') => (
    <div className="password-field">
      <input
        type={showPasswords[passwordKey] ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="response-input"
        autoComplete="new-password"
      />
      <button
        type="button"
        className="password-toggle"
        onClick={() => togglePassword(passwordKey)}
        title={showPasswords[passwordKey] ? 'Hide' : 'Show'}
      >
        {showPasswords[passwordKey] ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );

  const renderAddressForm = () => (
    <div className="composite-form">
      <div className="form-field">
        <label>Street Address</label>
        <input type="text" className="response-input" placeholder="123 Main St"
          value={responseData?.street || ''} onChange={(e) => updateResponseData('street', e.target.value)} />
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>City</label>
          <input type="text" className="response-input" placeholder="City"
            value={responseData?.city || ''} onChange={(e) => updateResponseData('city', e.target.value)} />
        </div>
        <div className="form-field form-field-sm">
          <label>State</label>
          <select className="response-select"
            value={responseData?.state || ''} onChange={(e) => updateResponseData('state', e.target.value)}>
            <option value="">--</option>
            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-field form-field-sm">
          <label>ZIP Code</label>
          <input type="text" className="response-input" placeholder="12345"
            maxLength={10} value={responseData?.zip || ''}
            onChange={(e) => updateResponseData('zip', e.target.value)} />
        </div>
      </div>
    </div>
  );

  const renderPrimaryContactForm = () => (
    <div className="composite-form">
      <div className="form-row">
        <div className="form-field">
          <label>First Name <span className="required-star">*</span></label>
          <input type="text" className="response-input" placeholder="First name"
            value={responseData?.first_name || ''} onChange={(e) => updateResponseData('first_name', e.target.value)} />
        </div>
        <div className="form-field">
          <label>Last Name <span className="required-star">*</span></label>
          <input type="text" className="response-input" placeholder="Last name"
            value={responseData?.last_name || ''} onChange={(e) => updateResponseData('last_name', e.target.value)} />
        </div>
      </div>
      <div className="form-field">
        <label>Email Address <span className="required-star">*</span></label>
        <input type="email" className="response-input" placeholder="email@example.com"
          value={responseData?.email || ''} onChange={(e) => updateResponseData('email', e.target.value)} />
      </div>
      <div className="form-field">
        <label>Phone Number</label>
        <input type="tel" className="response-input" placeholder="(555) 555-5555"
          value={responseData?.phone || ''} onChange={(e) => updateResponseData('phone', e.target.value)} />
      </div>
    </div>
  );

  const renderInvoiceNotificationsForm = () => (
    <div className="composite-form">
      <div className="form-field">
        <label>Email Address(es)</label>
        <p className="field-hint">Enter one email address per line.</p>
        <textarea
          className="response-textarea"
          rows={4}
          placeholder="billing@example.com&#10;accountant@example.com"
          value={responseText}
          onChange={(e) => setResponseText(e.target.value)}
        />
      </div>
    </div>
  );

  const renderDropdownForm = () => {
    const opts = activeQuestion?.dropdown_options || [];
    const hasOther = opts.includes('Other');
    return (
      <div className="composite-form">
        <select className="response-select" value={responseText}
          onChange={(e) => { setResponseText(e.target.value); if (e.target.value !== 'Other') setOtherText(''); }}>
          <option value="">Select an option...</option>
          {opts.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          {!hasOther && <option value="Other">Other</option>}
        </select>
        {responseText === 'Other' && (
          <div className="form-field" style={{ marginTop: '10px' }}>
            <label>Please specify:</label>
            <textarea className="response-textarea" rows={3}
              placeholder="Enter your answer..."
              value={otherText} onChange={(e) => setOtherText(e.target.value)} />
          </div>
        )}
      </div>
    );
  };

  const renderNASForm = () => {
    const hasNas = responseData?.has_nas;
    return (
      <div className="composite-form">
        {/* Step 1: Yes / No */}
        <div className="form-field">
          <label>Does this office have a NAS? <span className="required-star">*</span></label>
          <div className="radio-group">
            {['Yes', 'No'].map(opt => (
              <label key={opt} className="radio-label">
                <input
                  type="radio"
                  name="has_nas"
                  checked={hasNas === (opt === 'Yes')}
                  onChange={() => updateResponseData('has_nas', opt === 'Yes')}
                />
                {opt}
              </label>
            ))}
          </div>
        </div>

        {/* Step 2: NAS details — only shown when Yes */}
        {hasNas === true && (
          <>
            <div className="form-row">
              <div className="form-field">
                <label>Brand <span className="required-star">*</span></label>
                <input type="text" className="response-input" placeholder="e.g. Synology"
                  value={responseData?.brand || ''} onChange={(e) => updateResponseData('brand', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Model <span className="required-star">*</span></label>
                <input type="text" className="response-input" placeholder="e.g. DS923+"
                  value={responseData?.model || ''} onChange={(e) => updateResponseData('model', e.target.value)} />
              </div>
            </div>
            <div className="form-field">
              <label>Hostname <span className="required-star">*</span></label>
              <input type="text" className="response-input" placeholder="e.g. nas-storage"
                value={responseData?.hostname || ''} onChange={(e) => updateResponseData('hostname', e.target.value)} />
            </div>
            <div className="form-field">
              <label>IP Address <span className="required-star">*</span></label>
              <input type="text" className="response-input" placeholder="e.g. 192.168.1.100"
                pattern="^(\d{1,3}\.){3}\d{1,3}$" title="Enter a valid IPv4 address"
                value={responseData?.ip_address || ''} onChange={(e) => updateResponseData('ip_address', e.target.value)} />
            </div>
            <div className="form-section-title">Credentials</div>
            <div className="form-field">
              <label>Username</label>
              <input type="text" className="response-input" placeholder="Username"
                value={responseData?.username || ''} onChange={(e) => updateResponseData('username', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Password</label>
              {renderPasswordField('nas_password', responseData?.password || '', (v) => updateResponseData('password', v))}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderDomainControllerForm = () => (
    <div className="composite-form">
      <div className="form-field">
        <label>Domain Controller Present?</label>
        <div className="radio-group">
          {['Yes', 'No'].map(opt => (
            <label key={opt} className="radio-label">
              <input type="radio" name="dc_present"
                checked={responseData?.has_domain_controller === (opt === 'Yes')}
                onChange={() => updateResponseData('has_domain_controller', opt === 'Yes')} />
              {opt}
            </label>
          ))}
        </div>
      </div>
      {responseData?.has_domain_controller && (
        <>
          <div className="form-section-title">Domain Administrator Credentials</div>
          <div className="form-field">
            <label>Domain Administrator Username</label>
            <input type="text" className="response-input" placeholder="domain\administrator"
              value={responseData?.domain_admin_username || ''}
              onChange={(e) => updateResponseData('domain_admin_username', e.target.value)} />
          </div>
          <div className="form-field">
            <label>Domain Administrator Password</label>
            {renderPasswordField('dc_password', responseData?.domain_admin_password || '',
              (v) => updateResponseData('domain_admin_password', v))}
          </div>
        </>
      )}
    </div>
  );

  const renderServerListForm = () => {
    const hasServers = responseData?.has_servers;
    const servers = responseData?.servers || [];
    return (
      <div className="composite-form">
        {/* Step 1: Yes / No */}
        <div className="form-field">
          <label>Does this office have Servers? <span className="required-star">*</span></label>
          <div className="radio-group">
            {['Yes', 'No'].map(opt => (
              <label key={opt} className="radio-label">
                <input
                  type="radio"
                  name="has_servers"
                  checked={hasServers === (opt === 'Yes')}
                  onChange={() => updateResponseData('has_servers', opt === 'Yes')}
                />
                {opt}
              </label>
            ))}
          </div>
        </div>

        {/* Step 2: Server list — only shown when Yes */}
        {hasServers === true && servers.map((server, idx) => (
          <div key={idx} className="server-entry">
            <div className="server-entry-header">
              <span className="server-entry-title">Server {idx + 1}</span>
              {servers.length > 1 && (
                <button type="button" className="btn-remove-server" onClick={() => removeServer(idx)}>
                  <Trash2 size={14} /> Remove
                </button>
              )}
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Server Name <span className="required-star">*</span></label>
                <input type="text" className="response-input" placeholder="e.g. SRV-DC01"
                  value={server.name} onChange={(e) => updateServer(idx, 'name', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Operating System <span className="required-star">*</span></label>
                <input type="text" className="response-input" placeholder="e.g. Windows Server 2022"
                  value={server.os} onChange={(e) => updateServer(idx, 'os', e.target.value)} />
              </div>
            </div>
            <div className="form-field">
              <label>IP Address <span className="required-star">*</span></label>
              <input type="text" className="response-input" placeholder="e.g. 192.168.1.10"
                pattern="^(\d{1,3}\.){3}\d{1,3}$" title="Enter a valid IPv4 address"
                value={server.ip_address} onChange={(e) => updateServer(idx, 'ip_address', e.target.value)} />
            </div>
            <div className="form-field">
              <label className="checkbox-label">
                <input type="checkbox" checked={server.domain_joined}
                  onChange={(e) => updateServer(idx, 'domain_joined', e.target.checked)} />
                Domain Joined
              </label>
            </div>
            {!server.domain_joined && (
              <>
                <div className="form-field">
                  <label>Local Admin Username</label>
                  <input type="text" className="response-input" placeholder="Administrator"
                    value={server.local_admin_username}
                    onChange={(e) => updateServer(idx, 'local_admin_username', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Local Admin Password</label>
                  {renderPasswordField(`server_${idx}_password`, server.local_admin_password,
                    (v) => updateServer(idx, 'local_admin_password', v))}
                </div>
              </>
            )}
          </div>
        ))}
        {hasServers === true && (
          <button type="button" className="btn-add-server" onClick={addServer}>
            <Plus size={14} /> Add Another Server
          </button>
        )}
      </div>
    );
  };

  const renderVMListForm = () => {
    const hypervisorOptions = ['Hyper-V', 'VMware ESXi', 'Proxmox', 'XenServer', 'KVM', 'VirtualBox', 'Other'];
    const hasHypervisor = responseData?.has_hypervisor;
    const vms = responseData?.vms || [];
    return (
      <div className="composite-form">
        {/* Step 1: Yes / No */}
        <div className="form-field">
          <label>Does this office have a Hypervisor? <span className="required-star">*</span></label>
          <div className="radio-group">
            {['Yes', 'No'].map(opt => (
              <label key={opt} className="radio-label">
                <input
                  type="radio"
                  name="has_hypervisor"
                  checked={hasHypervisor === (opt === 'Yes')}
                  onChange={() => updateResponseData('has_hypervisor', opt === 'Yes')}
                />
                {opt}
              </label>
            ))}
          </div>
        </div>

        {/* Step 2: Hypervisor details — only shown when Yes */}
        {hasHypervisor === true && (
          <>
            <div className="form-section-title">Hypervisor Details</div>
            <div className="form-row">
              <div className="form-field">
                <label>Hypervisor Name <span className="required-star">*</span></label>
                <input type="text" className="response-input" placeholder="e.g. HV-PROD01"
                  value={responseData.hypervisor_name}
                  onChange={(e) => updateResponseData('hypervisor_name', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Hypervisor Type</label>
                <select className="response-input" value={responseData.hypervisor_type}
                  onChange={(e) => updateResponseData('hypervisor_type', e.target.value)}>
                  <option value="">Select...</option>
                  {hypervisorOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
            </div>
            <div className="form-field">
              <label>IP Address</label>
              <input type="text" className="response-input" placeholder="e.g. 192.168.1.50"
                value={responseData.hypervisor_ip}
                onChange={(e) => updateResponseData('hypervisor_ip', e.target.value)} />
            </div>
            <div className="form-section-title">Hypervisor Credentials</div>
            <div className="form-row">
              <div className="form-field">
                <label>Username</label>
                <input type="text" className="response-input" placeholder="e.g. administrator"
                  value={responseData.hypervisor_username}
                  onChange={(e) => updateResponseData('hypervisor_username', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Password</label>
                {renderPasswordField('hv_password', responseData.hypervisor_password,
                  (v) => updateResponseData('hypervisor_password', v))}
              </div>
            </div>

            {/* Step 3: VMs */}
            <div className="form-section-title">Virtual Machines</div>
            {vms.map((vm, idx) => (
              <div key={idx} className="server-entry">
                <div className="server-entry-header">
                  <span className="server-entry-title">VM {idx + 1}</span>
                  {vms.length > 1 && (
                    <button type="button" className="btn-remove-server" onClick={() => removeVM(idx)}>
                      <Trash2 size={14} /> Remove
                    </button>
                  )}
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label>VM Name <span className="required-star">*</span></label>
                    <input type="text" className="response-input" placeholder="e.g. VM-WebServer01"
                      value={vm.name} onChange={(e) => updateVM(idx, 'name', e.target.value)} />
                  </div>
                  <div className="form-field">
                    <label>Guest OS <span className="required-star">*</span></label>
                    <input type="text" className="response-input" placeholder="e.g. Windows Server 2022"
                      value={vm.guest_os} onChange={(e) => updateVM(idx, 'guest_os', e.target.value)} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label>IP Address</label>
                    <input type="text" className="response-input" placeholder="e.g. 192.168.1.20"
                      value={vm.ip_address} onChange={(e) => updateVM(idx, 'ip_address', e.target.value)} />
                  </div>
                  <div className="form-field form-field-sm">
                    <label>CPU Cores</label>
                    <input type="number" className="response-input" placeholder="4" min="1"
                      value={vm.cpu_cores} onChange={(e) => updateVM(idx, 'cpu_cores', e.target.value)} />
                  </div>
                  <div className="form-field form-field-sm">
                    <label>RAM (GB)</label>
                    <input type="number" className="response-input" placeholder="16" min="1"
                      value={vm.ram_gb} onChange={(e) => updateVM(idx, 'ram_gb', e.target.value)} />
                  </div>
                  <div className="form-field form-field-sm">
                    <label>Storage (GB)</label>
                    <input type="number" className="response-input" placeholder="500" min="1"
                      value={vm.storage_gb} onChange={(e) => updateVM(idx, 'storage_gb', e.target.value)} />
                  </div>
                </div>
                <div className="form-field">
                  <label>Purpose / Notes</label>
                  <input type="text" className="response-input" placeholder="e.g. Domain Controller, Web Server..."
                    value={vm.notes} onChange={(e) => updateVM(idx, 'notes', e.target.value)} />
                </div>
              </div>
            ))}
            <button type="button" className="btn-add-server" onClick={addVM}>
              <Plus size={14} /> Add Another VM
            </button>
          </>
        )}
      </div>
    );
  };

  // Render a read-only preview of previously submitted data for rejected questions
  const renderPreviousSubmission = (question) => {
    const response = question.current_response;
    if (!response) return null;
    const ft = question.field_type;

    const PreviewField = ({ label, value, isPassword, previewKey }) => {
      if (!value && value !== 0) return null;
      const masked = isPassword && !showPreviewPasswords[previewKey];
      return (
        <div className="preview-field-row">
          <span className="preview-field-label">{label}</span>
          <span className="preview-field-value">
            {masked ? '••••••••' : String(value)}
            {isPassword && (
              <button
                className="preview-password-toggle"
                onClick={(e) => { e.stopPropagation(); setShowPreviewPasswords(p => ({ ...p, [previewKey]: !p[previewKey] })); }}
                title={masked ? 'Show' : 'Hide'}
              >
                {masked ? <Eye size={13} /> : <EyeOff size={13} />}
              </button>
            )}
          </span>
        </div>
      );
    };

    const isPasswordKey = (key) => key.toLowerCase().includes('password');

    if (COMPOSITE_TYPES.includes(ft)) {
      const d = response.response_data;
      if (!d) return null;
      const rows = Object.entries(d).filter(([, v]) => v !== null && v !== '' && v !== undefined && !Array.isArray(v));
      const arrEntries = Object.entries(d).filter(([, v]) => Array.isArray(v));
      return (
        <div className="previous-submission">
          {rows.map(([k, v]) => (
            <PreviewField
              key={k}
              label={k.replace(/_/g, ' ')}
              value={typeof v === 'boolean' ? (v ? 'Yes' : 'No') : v}
              isPassword={isPasswordKey(k)}
              previewKey={`${question.id}_${k}`}
            />
          ))}
          {arrEntries.map(([k, arr]) =>
            arr.map((item, i) => (
              <div key={`${k}_${i}`} className="preview-list-item">
                <span className="preview-list-label">{k.replace(/_/g, ' ')} #{i + 1}</span>
                {Object.entries(item).filter(([, v]) => v !== '' && v !== null && v !== undefined).map(([ik, iv]) => (
                  <PreviewField
                    key={ik}
                    label={ik.replace(/_/g, ' ')}
                    value={typeof iv === 'boolean' ? (iv ? 'Yes' : 'No') : iv}
                    isPassword={isPasswordKey(ik)}
                    previewKey={`${question.id}_${k}_${i}_${ik}`}
                  />
                ))}
              </div>
            ))
          )}
        </div>
      );
    }

    if (response.response_text) {
      return (
        <div className="previous-submission">
          <PreviewField label="Previous answer" value={response.response_text} isPassword={false} />
        </div>
      );
    }

    if (response.file_name) {
      return (
        <div className="previous-submission">
          <PreviewField label="Uploaded file" value={response.file_name} isPassword={false} />
        </div>
      );
    }

    return null;
  };

  const renderResponseFormBody = (question) => {
    const ft = question.field_type;

    if (ft === 'document') {
      return (
        <div className="document-upload-section">
          {question.current_response?.file_name && !selectedFile && (
            <div className="uploaded-file-info">
              <FileText size={16} />
              <span>Current: {question.current_response.file_name}</span>
            </div>
          )}
          <div
            className={`file-drop-zone ${isDragging ? 'dragging' : ''} ${selectedFile ? 'has-file' : ''}`}
            onDrop={handleFileDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <input ref={fileInputRef} type="file" className="file-input-hidden"
              onChange={(e) => handleFileSelect(e.target.files[0])} />
            {selectedFile ? (
              <div className="selected-file-preview">
                <FileText size={24} />
                <div className="selected-file-info">
                  <span className="selected-file-name">{selectedFile.name}</span>
                  <span className="selected-file-size">{formatFileSize(selectedFile.size)}</span>
                </div>
                <button className="remove-file-btn" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}>
                  <X size={16} />
                </button>
              </div>
            ) : (
              <>
                <Upload size={28} />
                <p>Click to browse or drag and drop</p>
                <span className="file-hint">Max file size: 25MB</span>
              </>
            )}
          </div>
          {uploadError && (
            <div className="upload-error"><AlertCircle size={14} />{uploadError}</div>
          )}
          <div className="response-actions">
            <button className="cancel-button" onClick={() => { setActiveQuestion(null); setSelectedFile(null); setUploadError(''); }}>
              Cancel
            </button>
            <button className="submit-button" onClick={handleUploadDocument} disabled={isUploading || !selectedFile}>
              {isUploading ? <Loader2 className="spinner" size={16} /> : <><Upload size={16} />Upload</>}
            </button>
          </div>
        </div>
      );
    }

    let formBody;
    if (ft === 'address') formBody = renderAddressForm();
    else if (ft === 'primary_contact') formBody = renderPrimaryContactForm();
    else if (ft === 'invoice_notifications') formBody = renderInvoiceNotificationsForm();
    else if (ft === 'dropdown') formBody = renderDropdownForm();
    else if (ft === 'nas') formBody = renderNASForm();
    else if (ft === 'domain_controller') formBody = renderDomainControllerForm();
    else if (ft === 'server_list') formBody = renderServerListForm();
    else if (ft === 'vm_list') formBody = renderVMListForm();
    else if (ft === 'textarea') formBody = (
      <textarea value={responseText} onChange={(e) => setResponseText(e.target.value)}
        placeholder="Enter your response..." rows={4} className="response-textarea" />
    );
    else formBody = (
      <input type={ft === 'email' ? 'email' : ft === 'phone' ? 'tel' : 'text'}
        value={responseText} onChange={(e) => setResponseText(e.target.value)}
        placeholder="Enter your response..." className="response-input" />
    );

    return (
      <>
        {formBody}
        <div className="response-actions">
          <button className="cancel-button" onClick={() => { setActiveQuestion(null); setResponseText(''); setResponseData(null); setOtherText(''); }}>
            Cancel
          </button>
          <button className="submit-button" onClick={handleSubmitResponse} disabled={isSubmitting || !canSubmit()}>
            {isSubmitting ? <Loader2 className="spinner" size={16} /> : <><Send size={16} />Submit</>}
          </button>
        </div>
      </>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="project-loading">
        <Loader2 className="spinner-large" size={48} />
        <p>Loading project...</p>
      </div>
    );
  }

  const groupedQuestions = groupQuestionsByCategory();

  return (
    <div className="project-detail">
      <header className="project-header">
        <button className="back-button" onClick={() => navigate('/')}>
          <ArrowLeft size={20} />
          Back to Projects
        </button>
      </header>

      {/* In-project text notice — no email/SMS; the client learns of a file
          only by being here. Shown when there is at least one downloadable
          file. */}
      {availableDeliverableCount > 0 && (
        <div className="deliverable-banner">
          📎 {availableDeliverableCount} file{availableDeliverableCount === 1 ? '' : 's'} available
          to download — see the <strong>Downloads</strong> tab.
        </div>
      )}

      <div className="project-tabs">
        <button className={`tab ${activeTab === 'questions' ? 'active' : ''}`} onClick={() => setActiveTab('questions')}>
          Questions ({questions.length})
        </button>
        <button className={`tab ${activeTab === 'messages' ? 'active' : ''}`} onClick={() => setActiveTab('messages')}>
          <MessageCircle size={16} />
          Messages ({messages.length})
        </button>
        {deliverables.length > 0 && (
          <button className={`tab ${activeTab === 'downloads' ? 'active' : ''}`} onClick={() => setActiveTab('downloads')}>
            <FileText size={16} />
            Downloads ({availableDeliverableCount})
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {activeTab === 'downloads' ? (
        <div className="downloads-section">
          {deliverables.length === 0 ? (
            <div className="empty-messages">
              <FileText size={48} />
              <p>No files have been shared yet.</p>
            </div>
          ) : (
            <div className="deliverables-list">
              {deliverables.map((d) => (
                <div key={d.id} className={`deliverable-card ${d.available ? '' : 'expired'}`}>
                  <div className="deliverable-info">
                    <FileText size={22} />
                    <div>
                      <div className="deliverable-name">{d.file_name}</div>
                      {d.description && <div className="deliverable-desc">{d.description}</div>}
                      {d.available ? (
                        <div className="deliverable-meta">Available to download</div>
                      ) : (
                        <div className="deliverable-meta expired">
                          This download has expired. Contact Macula to have it re-sent.
                        </div>
                      )}
                    </div>
                  </div>
                  {d.available && (
                    <button className="download-button" onClick={() => handleDownloadDeliverable(d)}>
                      <Upload size={16} style={{ transform: 'rotate(180deg)' }} />
                      Download
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : activeTab === 'questions' ? (
        <div className="questions-section">
          {Object.entries(groupedQuestions).map(([category, categoryQuestions]) => (
            <div key={category} className="question-category">
              <h3 className="category-title">{category}</h3>
              <div className="questions-list">
                {categoryQuestions.map((question) => {
                  const response = question.current_response;
                  const status = response ? statusConfig[response.status] : statusConfig.pending;
                  const StatusIcon = status.icon;
                  const isActive = activeQuestion?.id === question.id;
                  const isApproved = response?.status === 'approved';
                  const isRejected = response?.status === 'rejected';

                  return (
                    <div
                      key={question.id}
                      className={`question-card ${isActive ? 'active' : ''} ${isApproved ? 'approved' : ''} ${isRejected && !isActive ? 'rejected-preview' : ''}`}
                      onClick={() => handleQuestionClick(question)}
                    >
                      <div className="question-header">
                        <div className="question-info">
                          <h4 className="question-label">{question.label}</h4>
                          {question.is_required && <span className="required-badge">Required</span>}
                        </div>
                        <div className="question-status" style={{ color: status.color }}>
                          <StatusIcon size={16} />
                          {status.label}
                        </div>
                      </div>

                      {question.description && (
                        <p className="question-description">{question.description}</p>
                      )}

                      {response?.rejection_reason && (
                        <div className="rejection-notice">
                          <AlertCircle size={14} />
                          {response.rejection_reason}
                        </div>
                      )}

                      {/* Read-only view of the submitted answer.
                          Shown for any state where a response exists and the
                          edit form isn't currently open — approved questions
                          have no form, so this is the ONLY way the client
                          can see what they sent. Rejected gets it too so
                          they can refer to the previous answer while fixing. */}
                      {response && !(isActive && !isApproved) && renderPreviousSubmission(question)}

                      {question.due_date && (
                        <div className="due-date">
                          Due: {new Date(question.due_date).toLocaleDateString()}
                        </div>
                      )}

                      {isActive && !isApproved && (
                        <div className="response-form" onClick={(e) => e.stopPropagation()}>
                          {renderResponseFormBody(question)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="messages-section">
          <div className="messages-list">
            {messages.length === 0 ? (
              <div className="empty-messages">
                <MessageCircle size={48} />
                <p>No messages yet. Start the conversation below.</p>
              </div>
            ) : (
              messages.map((msg) => (
                <div key={msg.id} className={`message ${msg.sender_type === 'client' ? 'sent' : 'received'}`}>
                  <div className="message-header">
                    <span className="sender-name">{msg.sender_type === 'client' ? 'You' : msg.sender_name || 'Staff'}</span>
                    <span className="message-time">{new Date(msg.created_at).toLocaleString()}</span>
                  </div>
                  <div className="message-content">{msg.message}</div>
                </div>
              ))
            )}
          </div>

          <div className="message-input-container">
            <textarea value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..." rows={2} className="message-input" />
            <button className="send-button" onClick={handleSendMessage} disabled={isSending || !newMessage.trim()}>
              {isSending ? <Loader2 className="spinner" size={20} /> : <Send size={20} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
